/**
 * Keys for other services (owner, 2026-10-13: an equipment register reads which computers and phones
 * are in the Ferma). Admin only. Read-only by construction — the service offers no other scopes — and
 * shown once: the service keeps a hash and a prefix, so a key nobody copied is revoked and reissued.
 *
 * Kept apart from My Claude on purpose: a personal key acts as a person over MCP and is refused by the
 * API directly; these are API credentials with nobody behind them, so they live where an admin sees all.
 */

import { useState } from 'react'

import type { ServiceKey } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Badge, Card, Empty, Pill, Skeletons, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'

export function ServiceKeysPanel() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const k = t.serviceKeys
  const keys = usePoll(() => api.serviceKeys(), 30000, [api, authEpoch], auth.isAdmin)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['read:hubs', 'read:devices'])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  //: The one time the secret exists outside the service. State only, never storage.
  const [issued, setIssued] = useState<string | null>(null)
  const [pending, setPending] = useState<{ row: ServiceKey; act: 'revoke' | 'delete' } | null>(null)

  if (!auth.isAdmin) {
    return <Card title={k.title}><Status text={t.users.adminOnly} bad /></Card>
  }

  const run = async (fn: () => Promise<unknown>) => {
    setProblem(null)
    setBusy(true)
    try {
      await fn()
      keys.refresh()
    } catch (e) {
      const detail = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
      setProblem(detail?.hint ?? (e instanceof Error ? e.message : String(e)))
    } finally {
      setBusy(false)
      setPending(null)
    }
  }

  const catalogue = Object.keys(keys.data?.scopes ?? { 'read:hubs': '', 'read:devices': '' })
  const copy = (text: string) => void navigator.clipboard?.writeText(text)

  return (
    <>
      <Card title={k.title} sub={k.sub}>
        <form className="col" onSubmit={(e) => {
          e.preventDefault()
          void run(async () => {
            setIssued((await api.createServiceKey(name.trim(), scopes)).token)
            setName('')
          })
        }}>
          <input value={name} placeholder={k.namePh} aria-label={k.name} maxLength={80}
                 onChange={(e) => setName(e.target.value)} />
          <div className="small">{k.scopes}</div>
          {catalogue.map((scope) => (
            <label key={scope} className="row">
              <input type="checkbox" checked={scopes.includes(scope)}
                     onChange={(e) => setScopes((now) =>
                       e.target.checked ? [...now, scope] : now.filter((s) => s !== scope))} />
              <span className="mono tiny">{scope}</span>
              <span className="small">{k.scope[scope] ?? keys.data?.scopes[scope]}</span>
            </label>
          ))}
          <div className="row">
            <button className="primary" type="submit" disabled={busy || !name.trim() || scopes.length === 0}>
              {k.create}
            </button>
            <span className="tiny dim">{k.shownOnce}</span>
          </div>
        </form>
        <Status text={problem} bad />
        {issued && (
          <>
            <div className="small">{k.issued}</div>
            <div className="row">
              <code className="grow mono" style={{ overflowWrap: 'anywhere' }}>{issued}</code>
              <button onClick={() => copy(issued)}>{k.copy}</button>
            </div>
          </>
        )}
      </Card>

      <Card title={k.list} sub={k.listSub}>
        {!keys.data && !keys.error && <Skeletons count={1} />}
        <Status text={keys.error ? keys.error.message : null} bad />
        {keys.data?.keys.length === 0 && <Empty>{k.none}</Empty>}
        {(keys.data?.keys ?? []).map((row) => (
          <div className="row" key={row.key_id}>
            <b>{row.name}</b>
            <Pill mono>{row.prefix}…</Pill>
            {row.scopes.map((s) => <Pill key={s} mono>{s}</Pill>)}
            {row.live ? <Badge kind="ok">{k.live}</Badge> : <Badge kind="warn">{k.revoked}</Badge>}
            <span className="tiny dim grow">
              {row.created_at.slice(0, 10)} · {k.by(row.owner ?? '—')} ·{' '}
              {row.last_used_at ? k.used(row.last_used_at.slice(0, 16).replace('T', ' ')) : k.neverUsed}
            </span>
            {row.live && (
              <button className="ghost tiny" disabled={busy}
                      onClick={() => setPending({ row, act: 'revoke' })}>{k.revoke}</button>
            )}
            <button className="ghost tiny" disabled={busy}
                    onClick={() => setPending({ row, act: 'delete' })}>{k.remove}</button>
          </div>
        ))}
      </Card>

      {pending && (
        <ConfirmDanger
          title={pending.row.name}
          confirmLabel={pending.act === 'revoke' ? k.revoke : k.remove}
          onCancel={() => setPending(null)}
          onConfirm={() => void run(() => pending.act === 'revoke'
            ? api.revokeServiceKey(pending.row.key_id) : api.deleteServiceKey(pending.row.key_id))}
        >
          <div>{pending.act === 'revoke' ? k.confirmRevoke : k.confirmRemove}</div>
        </ConfirmDanger>
      )}
    </>
  )
}
